import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  Chip,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  PhoneAndroid as PhoneIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  searchField: {
    marginBottom: theme.spacing(1),
  },
  list: {
    maxHeight: 400,
    overflow: "auto",
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  emptyText: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  statusChip: {
    height: 18,
    fontSize: "0.65rem",
    fontWeight: 600,
  },
  icon: {
    minWidth: 32,
  },
}));

const STATUS_COLORS = {
  CONNECTED: "#4CAF50",
  OPENING: "#FF9800",
  qrcode: "#FF9800",
  DISCONNECTED: "#F44336",
  TIMEOUT: "#F44336",
};

const ConnectionSelectionDialog = ({ open, onClose, onSelect }) => {
  const classes = useStyles();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchConnections = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        if (!cancelled) setConnections(data || []);
      } catch (err) {
        console.error("[ConnectionSelectionDialog] error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchConnections();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = search.trim()
    ? connections.filter((c) =>
        (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.number || "").toLowerCase().includes(search.toLowerCase())
      )
    : connections;

  const handleSelect = useCallback(
    (whatsappId) => {
      onSelect(whatsappId);
    },
    [onSelect]
  );

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Trocar Conexão</DialogTitle>
      <DialogContent>
        <TextField
          className={classes.searchField}
          placeholder="Buscar conexão..."
          fullWidth
          size="small"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <div className={classes.loadingCenter}>
            <CircularProgress size={24} />
          </div>
        ) : filtered.length === 0 ? (
          <Typography className={classes.emptyText}>
            Nenhuma conexão encontrada
          </Typography>
        ) : (
          <List className={classes.list} dense>
            {filtered.map((conn) => (
              <ListItem
                key={conn.id}
                button
                onClick={() => handleSelect(conn.id)}
                disabled={conn.status !== "CONNECTED"}
              >
                <ListItemIcon className={classes.icon}>
                  <PhoneIcon
                    style={{
                      color: STATUS_COLORS[conn.status] || "#9E9E9E",
                      fontSize: 20,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={conn.name}
                  secondary={conn.number || undefined}
                />
                <Chip
                  className={classes.statusChip}
                  label={conn.status === "CONNECTED" ? "Online" : conn.status}
                  size="small"
                  style={{
                    backgroundColor: STATUS_COLORS[conn.status] || "#9E9E9E",
                    color: "#fff",
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(ConnectionSelectionDialog);
